import { DateTime, Interval } from 'luxon';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { GlobalApi } from '../../../util/Global';
import { useContainer } from '../../../hooks';
import { useResizeListener } from '../../../hooks/windowEvents/useResizeListener';
import { SingleDay } from './SingleDay';

interface IRowDayPicker {
    value: DateTime;
    onChange: (date: DateTime) => void;
    isDayOff: (day: DateTime) => boolean;
}

type MyScrollBehavior = 'smooth' | 'auto';

export const RowDayPicker = ({ value, onChange, isDayOff }: IRowDayPicker) => {
    const [scrollBehavior, setScrollBehavior] = useState<MyScrollBehavior>('auto');
    const container = useRef<HTMLDivElement>(null);
    const selectedCell = useRef<HTMLButtonElement>(null);
    const [visibleInterval, setVisibleInterval] = useState<Interval>(
        Interval.fromDateTimes(DateTime.now().minus({ month: 3 }).startOf('month'), DateTime.now().plus({ month: 1 }).endOf('month'))
    );
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const startX = useRef<number>(0);
    const scrollLeft = useRef<number>(0);
    const isProgrammaticScroll = useRef<boolean>(false);
    const globalApi: GlobalApi = useContainer(GlobalApi);

    const centerOnSelected = () => {
        if (selectedCell.current && container.current) {
            isProgrammaticScroll.current = true;
            selectedCell.current.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });

            setTimeout(() => {
                isProgrammaticScroll.current = false;
            }, 600);
        }
    };

    useEffect(() => {
        const selectedDate = value.startOf('day');

        if (!visibleInterval.contains(selectedDate)) {
            let newStart = visibleInterval.start;
            let newEnd = visibleInterval.end;

            if (selectedDate < visibleInterval.start) {
                newStart = selectedDate.startOf('month').minus({ months: 1 });
            }

            if (selectedDate > visibleInterval.end) {
                newEnd = selectedDate.endOf('month').plus({ months: 1 });
            }

            setVisibleInterval(Interval.fromDateTimes(newStart, newEnd));
        }
    }, [value.toISO()]);

    useLayoutEffect(() => {
        const selectedDate = value.startOf('day');
        if (visibleInterval.contains(selectedDate)) {
            requestAnimationFrame(() => {
                centerOnSelected();
            });
        }
    }, [visibleInterval.start.toISO(), visibleInterval.end.toISO(), value.toISO()]);

    const scrollContainer = (toRight: boolean) => {
        if (!container.current?.scrollLeft) return;
        if (toRight) container.current.scrollLeft += 300;
        else container.current.scrollLeft -= 300;
    };

    const handleScroll = () => {
        if (container.current) {
            if (isProgrammaticScroll.current) {
                return;
            }

            if (scrollBehavior === 'auto') setScrollBehavior('smooth');
            const scrollPositionRight = container.current.clientWidth + container.current.scrollLeft;
            const scrollEnd = container.current.scrollWidth;
            const scrollPositionLeft = container.current.scrollLeft;

            if (scrollPositionRight >= scrollEnd - 10) {
                setVisibleInterval(prev => Interval.fromDateTimes(prev.start, prev.end.plus({ month: 1 })));
            }

            if (scrollPositionLeft <= 10) {
                setVisibleInterval(prev => Interval.fromDateTimes(prev.start.minus({ month: 1 }), prev.end));
            }
        }
    };

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (globalApi.innerWidth() < 768) {
            setIsDragging(true);

            if (container.current) {
                startX.current = event.pageX - container.current.offsetLeft;
                scrollLeft.current = container.current.scrollLeft;
            }
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!isDragging) {
            return;
        }

        event.preventDefault();
        if (container.current) {
            const offset = event.pageX - container.current.offsetLeft;
            const distance = offset - startX.current;

            container.current.scrollLeft = scrollLeft.current - distance;
        }
    };

    useResizeListener(innerWidth => {
        if (innerWidth < 768) setScrollBehavior('auto');
    });

    const daysWithDayOff = useMemo(() => {
        return visibleInterval.splitBy({ day: 1 }).map(day => ({
            day: day.start,
            isDayOff: isDayOff(day.start),
        }));
    }, [visibleInterval, isDayOff]);

    return (
        <div className="row-day-picker">
            <button onClick={() => scrollContainer(false)} className="row-day-picker__arrow">
                <img src="/svg/arrow-left-blue-2.svg" alt="Arrow" />
            </button>
            <div
                ref={container}
                style={{ scrollBehavior }}
                className="row-day-picker__container"
                onScrollCapture={handleScroll}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseUp}
            >
                {daysWithDayOff.map(({ day, isDayOff: dayOff }) => {
                    return (
                        <SingleDay
                            key={day.toISO()}
                            ref={day.equals(value.startOf('day')) ? selectedCell : undefined}
                            day={day}
                            selected={day.equals(value.startOf('day'))}
                            onClick={onChange}
                            isDayOff={dayOff}
                        />
                    );
                })}
            </div>
            <button className="row-day-picker__arrow row-day-picker__right-arrow" onClick={() => scrollContainer(true)}>
                <img src="/svg/arrow-left-blue-2.svg" alt="Arrow" />
            </button>
        </div>
    );
};
